import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Plus, Save, Trash2, GripVertical, Video, FileText, 
  BookOpen, Crown, Eye, EyeOff, Image, ChevronDown, ChevronUp, Upload, File, Loader2, Sparkles, LayoutTemplate
} from 'lucide-react';
import { toast } from 'sonner';
import SeoAssistant from '../components/SeoAssistant';
import axios from 'axios';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import LessonVideoSource from '../components/course/LessonVideoSource';
import QuizBuilder from '../components/course/QuizBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sortable wrapper for a single module card — wraps the entire module
 * (header + content). Passes dnd-kit listeners so we can show a dedicated
 * drag handle on the grip icon without making the whole header draggable
 * (which would swallow clicks on the inputs inside the header).
 *
 * IMPORTANT: This component MUST live at module scope, NOT inside
 * `CourseCreate`. Defining it inline created a fresh function identity on
 * every parent re-render, so React unmounted + remounted every module
 * card on each keystroke — which stole focus from module title inputs
 * and made typing more than one character impossible. The user reported
 * "LMS modules will not allow me to type in more than one".
 */
const SortableModuleItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-border rounded-lg overflow-hidden"
    >
      {typeof children === 'function' ? children({ attributes, listeners }) : children}
    </div>
  );
};

export default function CourseCreate() {
  const { courseId } = useParams();
  const isEdit = !!courseId;
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [course, setCourse] = useState({
    title: '',
    description: '',
    thumbnail_url: '',
    tier_required: 'growth',
    free_access_tiers: ['founder', 'growth'],
    category: 'general',
    tags: [],
    price: 0
  });
  const [modules, setModules] = useState([]);
  // Track IDs of modules/lessons the user removed so `saveCourse` can DELETE
  // them on the backend. Without this, removed items came back on next load
  // because the save endpoint was only POSTing new items — never deleting.
  const [deletedModuleIds, setDeletedModuleIds] = useState([]);
  const [deletedLessonIds, setDeletedLessonIds] = useState([]);
  const [expandedModule, setExpandedModule] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [uploadingPdf, setUploadingPdf] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateMenuFor, setTemplateMenuFor] = useState(null);
  const [quizBuilderLesson, setQuizBuilderLesson] = useState(null);
  const pdfInputRef = useRef(null);

  // dnd-kit sensors — allow mouse, touch, and keyboard drag of modules.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleModuleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setModules((prev) => {
      const oldIdx = prev.findIndex(m => m.id === active.id);
      const newIdx = prev.findIndex(m => m.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      const reordered = arrayMove(prev, oldIdx, newIdx);
      return reordered.map((m, i) => ({ ...m, order: i }));
    });
  };

  const categories = ['general', 'sales', 'marketing', 'leadership', 'finance', 'mindset', 'business'];

  const generateAIDescription = async () => {
    if (!course.title.trim()) {
      toast.error('Please enter a course title first');
      return;
    }
    setGeneratingAI(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ai/generate-course-description`,
        { title: course.title, category: course.category },
        { headers: getAuthHeader() }
      );
      if (res.data.success) {
        handleCourseChange('description', res.data.description);
        toast.success('AI description generated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate description');
    } finally {
      setGeneratingAI(false);
    }
  };

  const [generatingLessons, setGeneratingLessons] = useState(false);

  const generateAILessons = async (moduleId) => {
    if (!course.title.trim()) {
      toast.error('Please enter a course title first');
      return;
    }
    setGeneratingLessons(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ai/generate-lesson-outline`,
        { course_title: course.title, course_description: course.description, num_lessons: 5 },
        { headers: getAuthHeader() }
      );
      if (res.data.success && res.data.lessons?.length > 0) {
        const moduleIndex = modules.findIndex(m => m.id === moduleId);
        if (moduleIndex === -1) return;
        
        const newLessons = res.data.lessons.map((lesson, i) => ({
          id: `temp-lesson-${Date.now()}-${i}`,
          title: lesson.title,
          description: lesson.summary || '',
          content_type: lesson.content_type || 'text',
          video_url: '',
          text_content: '',
          duration_minutes: 0,
          order: modules[moduleIndex].lessons.length + i,
          is_preview: false,
          isNew: true
        }));
        
        setModules(modules.map(m =>
          m.id === moduleId
            ? { ...m, lessons: [...m.lessons, ...newLessons] }
            : m
        ));
        toast.success(`${newLessons.length} lesson outlines generated!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate lessons');
    } finally {
      setGeneratingLessons(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user && !(user.membership_tier === 'founder' || user.is_admin)) {
      toast.error('Only Founders and Admin can create courses');
      navigate('/courses');
      return;
    }
    if (isEdit && user) {
      fetchCourse();
    }
    // Fetch built-in lesson templates once
    if (user) {
      axios.get(`${API_URL}/api/lesson-templates`, { headers: getAuthHeader() })
        .then(res => setTemplates(res.data.templates || []))
        .catch(() => { /* non-fatal */ });
    }
  }, [user, authLoading, courseId]);

  const applyTemplate = (moduleId, templateId) => {
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    setModules((prev) => prev.map(m => {
      if (m.id !== moduleId) return m;
      const startOrder = m.lessons.length;
      const newLessons = tpl.lessons.map((l, i) => ({
        id: `temp-lesson-${Date.now()}-${i}`,
        title: l.title,
        description: l.summary,
        content_type: 'video',
        video_url: '',
        text_content: l.summary,
        duration_minutes: 0,
        order: startOrder + i,
        is_preview: false,
        isNew: true,
      }));
      return { ...m, lessons: [...m.lessons, ...newLessons] };
    }));
    setExpandedModule(moduleId);
    setTemplateMenuFor(null);
    toast.success(`Added ${tpl.lessons.length} lessons from "${tpl.label}"`);
  };

  // Sortable wrapper for a single module card — wraps the entire module
  // (header + content). Passes dnd-kit listeners so we can show a dedicated
  // drag handle on the grip icon without making the whole header draggable
  // (which would swallow clicks on the inputs inside the header).
  // See top-level SortableModuleItem defined outside the component tree
  // (moved out to prevent React from remounting the input on every render,
  // which was stealing focus after the first keystroke).

  const fetchCourse = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${API_URL}/api/courses/${courseId}`, { headers });
      const data = response.data;
      setCourse({
        title: data.title,
        description: data.description,
        thumbnail_url: data.thumbnail_url || '',
        tier_required: data.tier_required,
        free_access_tiers: data.free_access_tiers || ['founder', 'growth'],
        category: data.category,
        tags: data.tags || [],
        price: data.price || 0
      });
      
      // Backend nests lessons INSIDE each module (data.modules[].lessons).
      // Read them from there and preserve order.
      const modulesWithLessons = (data.modules || []).map(mod => ({
        ...mod,
        lessons: (mod.lessons || []).slice().sort((a, b) => a.order - b.order)
      }));
      setModules(modulesWithLessons.sort((a, b) => a.order - b.order));
      
      if (modulesWithLessons.length > 0) {
        setExpandedModule(modulesWithLessons[0].id);
      }
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = (field, value) => {
    setCourse(prev => ({ ...prev, [field]: value }));
  };

  const addTag = () => {
    if (tagInput.trim() && !course.tags.includes(tagInput.trim())) {
      setCourse(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setCourse(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const addModule = () => {
    const newModule = {
      id: `temp-${Date.now()}`,
      title: `Module ${modules.length + 1}`,
      description: '',
      order: modules.length,
      lessons: [],
      isNew: true
    };
    setModules([...modules, newModule]);
    setExpandedModule(newModule.id);
  };

  const updateModule = (moduleId, field, value) => {
    setModules(modules.map(m => 
      m.id === moduleId ? { ...m, [field]: value } : m
    ));
  };

  const removeModule = (moduleId) => {
    // Server-persisted module (not `temp-…`) — remember its id so save() can DELETE it.
    if (!moduleId.startsWith('temp-')) {
      setDeletedModuleIds((prev) => (prev.includes(moduleId) ? prev : [...prev, moduleId]));
    }
    setModules(modules.filter(m => m.id !== moduleId));
  };

  const addLesson = (moduleId) => {
    const moduleIndex = modules.findIndex(m => m.id === moduleId);
    const newLesson = {
      id: `temp-lesson-${Date.now()}`,
      title: `Lesson ${modules[moduleIndex].lessons.length + 1}`,
      description: '',
      content_type: 'video',
      video_url: '',
      text_content: '',
      duration_minutes: 0,
      order: modules[moduleIndex].lessons.length,
      is_preview: false,
      isNew: true
    };
    
    setModules(modules.map(m => 
      m.id === moduleId 
        ? { ...m, lessons: [...m.lessons, newLesson] }
        : m
    ));
  };

  const updateLesson = (moduleId, lessonId, field, value) => {
    setModules(modules.map(m => 
      m.id === moduleId 
        ? {
            ...m,
            lessons: m.lessons.map(l => 
              l.id === lessonId ? { ...l, [field]: value } : l
            )
          }
        : m
    ));
  };

  const removeLesson = (moduleId, lessonId) => {
    // Server-persisted lesson (not `temp-…`) — remember it so save() can DELETE it.
    if (!lessonId.startsWith('temp-')) {
      setDeletedLessonIds((prev) => (prev.includes(lessonId) ? prev : [...prev, lessonId]));
    }
    setModules(modules.map(m => 
      m.id === moduleId 
        ? { ...m, lessons: m.lessons.filter(l => l.id !== lessonId) }
        : m
    ));
  };

  const saveCourse = async (publish = false) => {
    if (!course.title.trim()) {
      toast.error('Course title is required');
      return;
    }

    setSaving(true);
    try {
      const headers = getAuthHeader();
      let savedCourseId = courseId;

      if (isEdit) {
        await axios.put(`${API_URL}/api/courses/${courseId}`, course, { headers });
      } else {
        const response = await axios.post(`${API_URL}/api/courses`, course, { headers });
        savedCourseId = response.data.course_id;
      }

      // 1. Delete removed lessons first — they may belong to modules we're
      //    about to delete, but deleting individually keeps quiz cascade clean.
      for (const lessonId of deletedLessonIds) {
        try {
          await axios.delete(`${API_URL}/api/lessons/${lessonId}`, { headers });
        } catch (err) {
          // A 404 means it was already gone — safe to swallow. Anything
          // else, log but keep saving so we don't strand the whole course.
          if (err?.response?.status !== 404) {
            console.error(`Failed to delete lesson ${lessonId}:`, err);
          }
        }
      }

      // 2. Delete removed modules — backend cascades lessons + quizzes.
      for (const moduleId of deletedModuleIds) {
        try {
          await axios.delete(`${API_URL}/api/modules/${moduleId}`, { headers });
        } catch (err) {
          if (err?.response?.status !== 404) {
            console.error(`Failed to delete module ${moduleId}:`, err);
          }
        }
      }

      // 3. Save modules and lessons (create new, update existing).
      for (let i = 0; i < modules.length; i++) {
        const module = modules[i];
        let savedModuleId = module.id;

        if (module.isNew || module.id.startsWith('temp-')) {
          const moduleRes = await axios.post(
            `${API_URL}/api/courses/${savedCourseId}/modules`,
            { title: module.title, description: module.description, order: i },
            { headers }
          );
          savedModuleId = moduleRes.data.module_id;
        } else {
          await axios.put(
            `${API_URL}/api/modules/${module.id}`,
            { title: module.title, description: module.description, order: i },
            { headers }
          );
        }

        // Save lessons — POST new ones, PUT updates for existing ones so
        // edits to title/video/description/duration/preview actually persist.
        for (let j = 0; j < module.lessons.length; j++) {
          const lesson = module.lessons[j];
          const lessonPayload = {
            title: lesson.title,
            description: lesson.description || '',
            content_type: lesson.content_type,
            video_url: lesson.video_url || null,
            text_content: lesson.text_content || null,
            pdf_url: lesson.pdf_url || null,
            duration_minutes: lesson.duration_minutes || 0,
            order: j,
            is_preview: !!lesson.is_preview,
          };
          if (lesson.isNew || lesson.id.startsWith('temp-')) {
            await axios.post(
              `${API_URL}/api/modules/${savedModuleId}/lessons`,
              lessonPayload,
              { headers }
            );
          } else {
            await axios.put(
              `${API_URL}/api/lessons/${lesson.id}`,
              lessonPayload,
              { headers }
            );
          }
        }
      }

      // Reset pending-delete queues once the save has completed successfully.
      setDeletedModuleIds([]);
      setDeletedLessonIds([]);

      // Publish if requested
      if (publish) {
        await axios.post(`${API_URL}/api/courses/${savedCourseId}/publish`, {}, { headers });
        toast.success('Course published successfully!');
      } else {
        toast.success('Course saved successfully!');
      }

      navigate(`/courses/${savedCourseId}`);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="wm-glass sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/courses">
                <Button variant="ghost" size="sm" className="text-foreground hover:text-primary">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </Link>
              <h1 className="text-xl font-playfair font-bold text-foreground">
                {isEdit ? 'Edit Course' : 'Create New Course'}
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => saveCourse(false)}
                disabled={saving}
                className="border-border text-foreground"
                data-testid="save-draft-btn"
              >
                <Save className="w-4 h-4 mr-2" /> Save Draft
              </Button>
              <Button
                onClick={() => saveCourse(true)}
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="publish-btn"
              >
                {saving ? 'Saving...' : 'Save & Publish'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="wm-card p-6 rounded-xl">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" /> Course Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title" className="text-foreground">Course Title *</Label>
                  <Input
                    id="title"
                    value={course.title}
                    onChange={(e) => handleCourseChange('title', e.target.value)}
                    placeholder="e.g., Mastering Sales: From Zero to Hero"
                    className="mt-1 bg-input border-border text-foreground"
                    data-testid="course-title-input"
                  />
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="description" className="text-foreground">Description</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={generateAIDescription}
                      disabled={generatingAI || !course.title.trim()}
                      className="text-primary hover:text-primary/80 hover:bg-primary/10 text-xs h-7"
                      data-testid="ai-generate-btn"
                    >
                      {generatingAI ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                      ) : (
                        <><Sparkles className="w-3 h-3 mr-1" /> AI Generate (Free · Claude)</>
                      )}
                    </Button>
                  </div>
                  <Textarea
                    id="description"
                    value={course.description}
                    onChange={(e) => handleCourseChange('description', e.target.value)}
                    placeholder="Describe what students will learn... or click 'AI Generate' to auto-create"
                    rows={6}
                    className="mt-1 bg-input border-border text-foreground"
                    data-testid="course-description-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="thumbnail" className="text-foreground">Thumbnail URL</Label>
                  <Input
                    id="thumbnail"
                    value={course.thumbnail_url}
                    onChange={(e) => handleCourseChange('thumbnail_url', e.target.value)}
                    placeholder="https://..."
                    className="mt-1 bg-input border-border text-foreground"
                  />
                  {course.thumbnail_url && (
                    <div className="mt-2 rounded-lg overflow-hidden h-32 bg-slate-800">
                      <img src={course.thumbnail_url} alt="Thumbnail" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modules & Lessons */}
            <div className="wm-card p-6 rounded-xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Video className="w-5 h-5 text-primary" /> Course Content
                </h2>
                <Button
                  onClick={addModule}
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="add-module-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Module
                </Button>
              </div>

              {modules.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
                  <BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">No modules yet. Add your first module to get started.</p>
                  <Button onClick={addModule} variant="outline" className="border-primary text-primary">
                    <Plus className="w-4 h-4 mr-2" /> Add First Module
                  </Button>
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleModuleDragEnd}>
                  <SortableContext items={modules.map(m => m.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-4">
                      {modules.map((module, moduleIndex) => (
                        <SortableModuleItem key={module.id} id={module.id}>
                          {({ attributes, listeners }) => (
                            <div data-testid={`module-editor-${moduleIndex}`}>
                              {/* Module Header */}
                              <div className="bg-slate-800/50 p-4 flex items-center gap-3">
                                <button
                                  type="button"
                                  {...attributes}
                                  {...listeners}
                                  className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
                                  aria-label="Drag to reorder"
                                  data-testid={`module-drag-handle-${moduleIndex}`}
                                >
                                  <GripVertical className="w-5 h-5" />
                                </button>
                                <div className="flex-1">
                                  <Input
                                    value={module.title}
                                    onChange={(e) => updateModule(module.id, 'title', e.target.value)}
                                    className="bg-transparent border-0 text-foreground font-semibold p-0 h-auto focus-visible:ring-0"
                                    placeholder="Module Title"
                                  />
                                </div>
                                <div className="relative">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setTemplateMenuFor(templateMenuFor === module.id ? null : module.id)}
                                    className="text-gold hover:bg-gold/10"
                                    data-testid={`template-btn-${moduleIndex}`}
                                    title="Insert lessons from a template"
                                  >
                                    <LayoutTemplate className="w-4 h-4 mr-1" /> Template
                                  </Button>
                                  {templateMenuFor === module.id && (
                                    <div className="absolute right-0 top-full mt-1 z-20 w-72 bg-card border border-border rounded-sm shadow-xl p-1">
                                      {templates.length === 0 && (
                                        <p className="p-3 text-xs text-muted-foreground">Loading templates…</p>
                                      )}
                                      {templates.map(t => (
                                        <button
                                          key={t.id}
                                          type="button"
                                          onClick={() => applyTemplate(module.id, t.id)}
                                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted rounded-sm"
                                          data-testid={`template-option-${t.id}`}
                                        >
                                          <div className="font-medium text-foreground">{t.label}</div>
                                          <div className="text-xs text-muted-foreground">{t.lessons.length} lessons · {t.description}</div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setExpandedModule(expandedModule === module.id ? null : module.id)}
                                >
                                  {expandedModule === module.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeModule(module.id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>

                      {/* Module Content */}
                      {expandedModule === module.id && (
                        <div className="p-4 space-y-4">
                          <Textarea
                            value={module.description}
                            onChange={(e) => updateModule(module.id, 'description', e.target.value)}
                            placeholder="Module description (optional)"
                            rows={2}
                            className="bg-input border-border text-foreground text-sm"
                          />

                          {/* Lessons */}
                          <div className="space-y-3">
                            {module.lessons.map((lesson, lessonIndex) => (
                              <div key={lesson.id} className="bg-slate-900/50 p-4 rounded-lg" data-testid={`lesson-editor-${lessonIndex}`}>
                                <div className="flex items-start gap-3">
                                  <span className="w-6 h-6 rounded bg-primary/20 text-primary text-xs flex items-center justify-center mt-1">
                                    {lessonIndex + 1}
                                  </span>
                                  <div className="flex-1 space-y-3">
                                    <Input
                                      value={lesson.title}
                                      onChange={(e) => updateLesson(module.id, lesson.id, 'title', e.target.value)}
                                      className="bg-input border-border text-foreground"
                                      placeholder="Lesson Title"
                                    />
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <Label className="text-xs text-muted-foreground">Content Type</Label>
                                        <select
                                          value={lesson.content_type}
                                          onChange={(e) => updateLesson(module.id, lesson.id, 'content_type', e.target.value)}
                                          className="w-full mt-1 bg-input border border-border rounded-md px-3 py-2 text-foreground text-sm"
                                        >
                                          <option value="video">Video</option>
                                          <option value="text">Text</option>
                                          <option value="pdf">PDF</option>
                                          <option value="quiz">Quiz</option>
                                        </select>
                                      </div>
                                      <div>
                                        <Label className="text-xs text-muted-foreground">Duration (min)</Label>
                                        <Input
                                          type="number"
                                          value={lesson.duration_minutes}
                                          onChange={(e) => updateLesson(module.id, lesson.id, 'duration_minutes', parseInt(e.target.value) || 0)}
                                          className="mt-1 bg-input border-border text-foreground"
                                        />
                                      </div>
                                    </div>
                                    {lesson.content_type === 'video' && (
                                      <LessonVideoSource
                                        value={lesson.video_url}
                                        onChange={(newUrl) => updateLesson(module.id, lesson.id, 'video_url', newUrl)}
                                        getAuthHeader={getAuthHeader}
                                      />
                                    )}
                                    {lesson.content_type === 'text' && (
                                      <Textarea
                                        value={lesson.text_content}
                                        onChange={(e) => updateLesson(module.id, lesson.id, 'text_content', e.target.value)}
                                        placeholder="Lesson content..."
                                        rows={6}
                                        className="bg-input border-border text-foreground"
                                      />
                                    )}
                                    {lesson.content_type === 'pdf' && (
                                      <div className="space-y-2">
                                        {lesson.pdf_url ? (
                                          <div className="flex items-center gap-3 p-3 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
                                            <File className="w-5 h-5 text-emerald-400" />
                                            <span className="text-emerald-400 text-sm flex-1">PDF uploaded</span>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => updateLesson(module.id, lesson.id, 'pdf_url', '')}
                                              className="text-red-400 hover:text-red-300"
                                            >
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          </div>
                                        ) : (
                                          <div>
                                            <input
                                              type="file"
                                              accept=".pdf"
                                              className="hidden"
                                              ref={pdfInputRef}
                                              onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                setUploadingPdf(lesson.id);
                                                try {
                                                  const formData = new FormData();
                                                  formData.append('file', file);
                                                  
                                                  // If lesson already saved, upload directly
                                                  if (!lesson.id.startsWith('temp-')) {
                                                    const res = await axios.post(
                                                      `${API_URL}/api/lessons/${lesson.id}/upload-pdf`,
                                                      formData,
                                                      { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                                                    );
                                                    updateLesson(module.id, lesson.id, 'pdf_url', res.data.pdf_url);
                                                    toast.success('PDF uploaded!');
                                                  } else {
                                                    toast.info('Save the course first, then upload the PDF');
                                                  }
                                                } catch (err) {
                                                  toast.error('Failed to upload PDF');
                                                } finally {
                                                  setUploadingPdf(null);
                                                  e.target.value = '';
                                                }
                                              }}
                                            />
                                            <Button
                                              variant="outline"
                                              className="w-full border-dashed border-border text-muted-foreground hover:text-foreground"
                                              onClick={() => pdfInputRef.current?.click()}
                                              disabled={uploadingPdf === lesson.id}
                                              data-testid="upload-pdf-btn"
                                            >
                                              {uploadingPdf === lesson.id ? (
                                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                                              ) : (
                                                <><Upload className="w-4 h-4 mr-2" /> Upload PDF Document</>
                                              )}
                                            </Button>
                                            {lesson.id.startsWith('temp-') && (
                                              <p className="text-xs text-amber-400 mt-1">Save the course first to enable PDF upload</p>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    {lesson.content_type === 'quiz' && (
                                      <div className="rounded-lg border border-brand-gold/30 bg-gradient-to-r from-brand-gold/10 to-transparent p-4">
                                        <div className="flex items-start gap-3">
                                          <div className="w-10 h-10 rounded-lg bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
                                            <Sparkles className="w-5 h-5 text-brand-gold" />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-foreground">Quiz-only lesson</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                              Students take a quiz to complete this lesson. Passing at 80% (default) unlocks the next lesson.
                                            </p>
                                            {lesson.id.startsWith('temp-') ? (
                                              <p className="mt-2 text-xs text-amber-400">Save the course first to configure the quiz.</p>
                                            ) : (
                                              <Button
                                                type="button"
                                                size="sm"
                                                onClick={() => setQuizBuilderLesson({ ...lesson, courseTitle: course.title })}
                                                className="mt-3 bg-brand-gold text-black hover:brightness-110"
                                                data-testid={`configure-quiz-${lesson.id}`}
                                              >
                                                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Configure Quiz
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id={`preview-${lesson.id}`}
                                        checked={lesson.is_preview}
                                        onChange={(e) => updateLesson(module.id, lesson.id, 'is_preview', e.target.checked)}
                                        className="rounded border-border"
                                      />
                                      <Label htmlFor={`preview-${lesson.id}`} className="text-sm text-muted-foreground cursor-pointer">
                                        Free preview (visible to non-members)
                                      </Label>
                                      {/* Quiz builder trigger for non-quiz lessons — attach a quiz to any lesson */}
                                      {!lesson.id.startsWith('temp-') && lesson.content_type !== 'quiz' && (
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          onClick={() => setQuizBuilderLesson({ ...lesson, courseTitle: course.title })}
                                          className="ml-auto border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
                                          data-testid={`open-quiz-builder-${lesson.id}`}
                                        >
                                          <Sparkles className="w-3.5 h-3.5 mr-1" /> Attach Quiz
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeLesson(module.id, lesson.id)}
                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="flex gap-2">
                            <Button
                              onClick={() => addLesson(module.id)}
                              variant="outline"
                              size="sm"
                              className="flex-1 border-dashed border-border text-muted-foreground hover:text-foreground"
                              data-testid="add-lesson-btn"
                            >
                              <Plus className="w-4 h-4 mr-2" /> Add Lesson
                            </Button>
                            <Button
                              onClick={() => generateAILessons(module.id)}
                              variant="outline"
                              size="sm"
                              disabled={generatingLessons || !course.title.trim()}
                              className="border-primary/30 text-primary hover:bg-primary/10"
                              data-testid="ai-lessons-btn"
                            >
                              {generatingLessons ? (
                                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</>
                              ) : (
                                <><Sparkles className="w-4 h-4 mr-1" /> AI Lessons (Free · Claude)</>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                            </div>
                          )}
                        </SortableModuleItem>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Settings */}
            <div className="wm-card p-6 rounded-xl">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" /> Course Settings
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-foreground">Access Tier</Label>
                  <select
                    value={course.tier_required}
                    onChange={(e) => handleCourseChange('tier_required', e.target.value)}
                    className="w-full mt-1 bg-input border border-border rounded-md px-3 py-2 text-foreground"
                    data-testid="tier-select"
                  >
                    <option value="ascend">Ascend (Basic)</option>
                    <option value="growth">Growth (Premium)</option>
                    <option value="founder">Founder (VIP)</option>
                  </select>
                </div>

                <div>
                  <Label className="text-foreground">Category</Label>
                  <select
                    value={course.category}
                    onChange={(e) => handleCourseChange('category', e.target.value)}
                    className="w-full mt-1 bg-input border border-border rounded-md px-3 py-2 text-foreground"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label className="text-foreground">Free Access — Which Tiers?</Label>
                  <div className="mt-2 space-y-2 border border-border rounded-md p-3 bg-input/30">
                    {[
                      { id: 'founder', label: 'Founders' },
                      { id: 'growth', label: 'Growth' },
                      { id: 'ascend', label: 'Ascend' },
                      { id: 'free', label: 'Free / No tier' },
                    ].map(t => {
                      const checked = (course.free_access_tiers || []).includes(t.id);
                      return (
                        <label key={t.id} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`free-tier-${t.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const cur = new Set(course.free_access_tiers || []);
                              if (e.target.checked) cur.add(t.id); else cur.delete(t.id);
                              handleCourseChange('free_access_tiers', Array.from(cur));
                            }}
                            className="w-4 h-4 accent-gold"
                          />
                          <span className="text-foreground">{t.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Tiers checked here get free access. Unchecked tiers must pay the price below.
                  </p>
                </div>

                <div>
                  <Label className="text-foreground">Price (Optional)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={course.price}
                      onChange={(e) => handleCourseChange('price', parseFloat(e.target.value) || 0)}
                      className="pl-7 bg-input border-border text-foreground"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Set to 0 for free access</p>
                </div>

                <div>
                  <Label className="text-foreground">Tags</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addTag()}
                      placeholder="Add tag..."
                      className="bg-input border-border text-foreground"
                    />
                    <Button onClick={addTag} variant="outline" className="border-border">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {course.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {course.tags.map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full flex items-center gap-1"
                        >
                          {tag}
                          <button onClick={() => removeTag(tag)} className="hover:text-primary/70">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {courseId && (
              <SeoAssistant
                title="SEO & AI Search (GEO) for this course"
                description="Let AI (GPT) suggest a search-optimized title, meta description, keywords and an AI-search (GEO) summary so this course gets found on Google and cited by AI search."
                suggestUrl={`${API_URL}/api/seo/suggest/course/${courseId}`}
                saveUrl={`${API_URL}/api/seo/course/${courseId}`}
                authHeader={getAuthHeader()}
              />
            )}

            {/* Summary */}
            <div className="wm-card p-6 rounded-xl">
              <h2 className="text-lg font-semibold text-foreground mb-4">Summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Modules</span>
                  <span className="text-foreground">{modules.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Lessons</span>
                  <span className="text-foreground">{modules.reduce((sum, m) => sum + m.lessons.length, 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Duration</span>
                  <span className="text-foreground">
                    {modules.reduce((sum, m) => sum + m.lessons.reduce((s, l) => s + (l.duration_minutes || 0), 0), 0)} min
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {quizBuilderLesson && (
        <QuizBuilder
          lesson={quizBuilderLesson}
          courseTitle={course.title}
          getAuthHeader={getAuthHeader}
          onClose={() => setQuizBuilderLesson(null)}
          onSaved={() => setQuizBuilderLesson(null)}
        />
      )}
    </div>
  );
}
